import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["status"]

  static values = {
    appName: String,
    appIconUrl: { type: String, default: "/apple-touch-icon.png" },
    assetUrl: { type: String, default: "/add-to-homescreen/img/" },
    maxModalDisplayCount: { type: Number, default: 3 },
    fallbackText: String
  }

  connect() {
    // Reset display count so the guide always shows on this page
    localStorage.removeItem("adhs-modal-display-count")

    // Defer initialization until the external script has loaded, then auto-show
    if (typeof window.AddToHomeScreen === "function") {
      this._init()
      this.show()
    } else {
      // Script loaded with defer — wait for it
      document.querySelector('script[src*="add-to-homescreen"]')
        ?.addEventListener("load", () => { this._init(); this.show() })
      // Fallback: poll briefly in case load already fired
      this._pollForLibrary(10)
    }
  }

  show(event) {
    event?.preventDefault()

    if (!this.instance) {
      this._init()
    }
    if (!this.instance) {
      this.markUnavailable()
      return
    }

    // The library uses the URL's `locale` param to pick a language.
    // Rails uses `vi` but the library's Vietnamese catalog key is `vn` — remap it.
    this._withRemappedLocale("vi", "vn", () => this.instance.show())
  }

  _init() {
    this.instance = this._resolveInstance()
    if (!this.instance) {
      this.markUnavailable()
    }
  }

  _pollForLibrary(attempts) {
    if (attempts <= 0) {
      this.markUnavailable()
      return
    }
    if (typeof window.AddToHomeScreen === "function") {
      this._init()
      this.show()
      return
    }
    setTimeout(() => this._pollForLibrary(attempts - 1), 100)
  }

  _resolveInstance() {
    if (typeof window.AddToHomeScreen !== "function") return null

    if (!window.bscaloAddToHomeScreenInstance) {
      try {
        window.bscaloAddToHomeScreenInstance = window.AddToHomeScreen({
          appName: this.appNameValue,
          appIconUrl: this.appIconUrlValue,
          assetUrl: this.assetUrlValue,
          maxModalDisplayCount: this.maxModalDisplayCountValue,
          allowClose: true,
          showArrow: true,
          showDesktop: true
        })
      } catch (e) {
        console.warn("AddToHomeScreen init failed:", e)
        return null
      }
    }

    this.clearUnavailableState()
    return window.bscaloAddToHomeScreenInstance
  }

  // Temporarily swaps `from` locale to `to` in the URL so the library picks up
  // the correct catalog, then restores the original value.
  _withRemappedLocale(from, to, fn) {
    const url = new URL(window.location.href)
    const current = url.searchParams.get("locale")
    const needsRemap = current === from

    if (needsRemap) {
      url.searchParams.set("locale", to)
      history.replaceState(null, "", url)
    }

    try {
      fn()
    } finally {
      if (needsRemap) {
        url.searchParams.set("locale", from)
        history.replaceState(null, "", url)
      }
    }
  }

  markUnavailable() {
    if (this.hasStatusTarget) {
      this.statusTarget.textContent = this.fallbackTextValue
      this.statusTarget.classList.remove("text-muted")
      this.statusTarget.classList.add("text-danger")
    }
  }

  clearUnavailableState() {
    if (this.hasStatusTarget) {
      this.statusTarget.classList.remove("text-danger")
      this.statusTarget.classList.add("text-muted")
    }
  }
}
